import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { observer } from 'mobx-react'
import { v4 as uuidv4 } from 'uuid'
import { makeStyles } from '@material-ui/core/styles'
import { useStore } from '../../../Models/RootStore'
import { PageHeader, Button,  Input, PollForm, PageContainer, PageContent, PageActions } from '../../../Components'
import { Colors, Fonts } from '../../../Utils/theme'
import { validatePoll } from '../../../Utils/validation'

const useStyles = makeStyles((theme) => ({
  text: {
    margin: 0,
    fontFamily: Fonts.content,
    fontSize: '1.125rem',
    fontWeight: 300,
    color: Colors.black,
    marginBottom: '1.5rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem'
    }
  },
  boldText: {
    fontWeight: 700
  },
  verticalSpacer: {
    height: 30
  },
  publicationContainer: {
    padding: '2rem 1rem 1rem',
    backgroundColor: Colors.cardBackgroundColor,
    border: `1px solid ${Colors.lightBorder}`,
    borderRadius: 5,
    marginBottom: '1rem'
  }
}))

const CreatePoll = (props) => {
  const classes = useStyles()
  const { pollStore } = useStore()

  useEffect(() => {
    if (pollStore.createOk) {
      props.history.push('/polls')
    }
  }, [pollStore.createOk])

  const [titleFi, setTitleFi] = useState('')
  const [titleEn, setTitleEn] = useState('')
  const [contentFi, setContentFi] = useState('')
  const [contentEn, setContentEn] = useState('')
  const [validFrom, setValidFrom] = useState(moment().format('YYYY-MM-DD'))
  const [validUntil, setValidUntil] = useState(null)

  const [multiAnswer, setMultiAnswer] = useState(false)
  const [draft, setDraft] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Initialize with one empty question
  const [questions, setQuestions] = useState([{
    id: uuidv4(),
    type: 'select',
    titleFi: '',
    titleEn: '',
    contentFi: '',
    contentEn: '',
    answersFi: ['', ''],
    answersEn: ['', '']
  }])

  const handleSetValidFrom = (val) => setValidFrom(val)
  const handleSetValidUntil = (val) => setValidUntil(val)

  const { t } = useTranslation()

  const createPoll = () => {
    const pollData = {
      draft,
      multiAnswer,
      titleFi,
      titleEn,
      contentFi,
      contentEn,
      validFrom: validFrom ? moment(validFrom).startOf('day').toISOString() : validFrom,
      validUntil: validUntil ? moment(validUntil).endOf('day').toISOString() : validUntil,
      questions
    }

    // Force draft as false
    const validFi = !validatePoll({ ...pollData, draft: false }, ['fi'])
    const validEn = !validatePoll({ ...pollData, draft: false }, ['fi', 'en'])

    if (validFi && !validEn) {
      if (window.confirm(t('confirm_missing_en_fields_copy_fi'))) {
        // Fallback to Finnish fields
        if (!titleEn) pollData.titleEn = titleFi
        if (!contentEn) pollData.contentEn = contentFi
        pollData.questions = questions.map(q => {
          if (!q.titleEn) q.titleEn = q.titleFi
          if (!q.contentEn) q.contentEn = q.contentFi
          q.answersEn = q.answersEn.map((a, index) => {
            if (!a) return q.answersFi[index]
            return a
          })
          return q
        })
      } else {
        // Cancel save
        return
      }
    }

    setSubmitted(true)
    pollStore.createPoll(pollData)
  }

  const renderContent = () => {
    return (
      <PollForm
        titleFi={titleFi}
        setTitleFi={setTitleFi}
        titleEn={titleEn}
        setTitleEn={setTitleEn}
        contentFi={contentFi}
        setContentFi={setContentFi}
        contentEn={contentEn}
        setContentEn={setContentEn}
        questions={questions}
        setQuestions={setQuestions}
        multiAnswer={multiAnswer}
        setMultiAnswer={setMultiAnswer}
        submitted={submitted}
      />
    )
  }

  const validationMessage = (field) => {
    if (submitted && !field) {
      return {
        error: true,
        helperText: t('required_field')
      }
    }
    return {}
  }

  const renderDraftCheckbox = () => {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!draft}
            onChange={() => setDraft(!draft)}
            name={t('save_as_draft')}
          />
        }
        label={t('save_as_draft')}
      />
    )
  }

  return (
    <>
      <PageHeader
        title={t('create_poll')}
      />
      <PageContainer>
        <PageContent>
          {renderContent()}
        </PageContent>
        <PageActions>
          <p className={[classes.text, classes.boldText].join(' ')}>
            {t('publication')}
          </p>
          <div className={classes.publicationContainer}>
            <Input
              type='date'
              label={t('valid_from')}
              value={validFrom}
              onChange={handleSetValidFrom}
              {...validationMessage(validFrom)}
            />
            <Input
              type='date'
              label={t('valid_until')}
              value={validUntil}
              onChange={handleSetValidUntil}
              {...validationMessage(validUntil)}
            />
          </div>
          {renderDraftCheckbox()}
          <Button
            text={t('save')}
            onClick={createPoll}
            margin
            fullWidth
          />
        </PageActions>
      </PageContainer>
    </>
  )
}

export default observer(CreatePoll)
